import styled from '@emotion/styled/macro';
import PropTypes from 'prop-types';
import { colors, themeLookup } from '../../styles/theme';
import { Icon } from '../icons';
import customIcons from '../../styles/icons';
import { Tooltip } from '../tooltip';

export const PositionWrapper = styled('div')`
  position: relative;
  height: ${(props) => `${props.size}px`};
`;

// medium default sized
export const AvatarWrapper = styled('div')`
  display: block;
  width: ${(props) => `${props.size}px`};
  height: ${(props) => `${props.size}px`};
  line-height: ${(props) => `${props.size}px`};
  position: ${(props) => `${props.position}`};
`;

export const AvatarImg = styled('img')`
  background: ${colors.white};
  border-radius: ${(props) => (props.square ? '' : '50%')};
  font-size: 24px;
  width: ${(props) => `${props.size}px`};
  height: ${(props) => `${props.size}px`};
  line-height: ${(props) => `${props.size}px`};
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-block;
  vertical-align: top;
  text-align: center;
  object-fit: cover;
`;

const AvatarDefault = styled(Icon)`
  background: ${colors.grey.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  width: ${(props) => `${props.size}px`};
  height: ${(props) => `${props.size}px`};
  line-height: ${(props) => `${props.size}px`};
  border-radius: 50%;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-block;
  vertical-align: top;
  text-align: center;
  &::before {
    display: block;
    content: '${customIcons.user}';
    color: ${colors.white};
    text-align: center;
    margin: 0 auto;
    font-size: 20px;
  }
`;

const AvatarBadge = styled('span')`
  color: white;
  background-color: ${(props) => props.backgroundColor};
  display: inline-block;
  font-weight: 500;
  border-radius: 2px;
  line-height: 6px;
  letter-spacing: 0.05em;
  padding: 2px 4px;
  text-transform: uppercase;
  display: inline-block;
  position: absolute;
  right: 0;
  bottom: 0;
  top: auto;
  font-size: 6px;
  text-align: center;
`;

function getPx(size) {
  return { xsmall: 22, small: 30, large: 40, xlarge: 50 }[size];
}

const Avatar = (props) => {
  const { square, src, alt, badgeText, badgeColor, tooltip, size, position } = props;
  const { text, bottom, left } = tooltip;

  let innerAvatar = <AvatarDefault size={getPx(size)} />;

  if (src) innerAvatar = <AvatarImg square={square} src={src} alt={alt} size={getPx(size)} />;

  if (badgeText && badgeText.length) {
    const colorCode = badgeColor.startsWith('#') ? badgeColor : themeLookup(colors, badgeColor);
    innerAvatar = (
      <>
        {innerAvatar}
        <AvatarBadge backgroundColor={colorCode} size={getPx(size)}>
          {badgeText}
        </AvatarBadge>
      </>
    );
  }

  innerAvatar = (
    <PositionWrapper size={getPx(size)}>
      <AvatarWrapper size={getPx(size)} position={position}>
        {innerAvatar}
      </AvatarWrapper>
    </PositionWrapper>
  );

  if (text && text.length) {
    return (
      <Tooltip position="start" bottom={bottom} left={left} content={text}>
        {innerAvatar}
      </Tooltip>
    );
  }

  return innerAvatar;
};

Avatar.defaultProps = {
  square: false,
  src: '',
  className: '',
  alt: '',
  badgeText: '',
  badgeColor: 'blue.primary',
  tooltip: {
    text: '',
    left: '',
    bottom: '',
  },
  size: 'large',
  position: 'absolute',
};

Avatar.propTypes = {
  square: PropTypes.bool,
  position: PropTypes.string,
  src: PropTypes.string,
  className: PropTypes.string,
  alt: PropTypes.string,
  badgeText: PropTypes.string,
  badgeColor: PropTypes.string,
  tooltip: PropTypes.shape({
    left: PropTypes.string,
    bottom: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
  size: PropTypes.string,
};

export default Avatar;
